import { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { lazyWithReload } from '@/lib/chunkReload';
import { AuthProvider } from '@/contexts/AuthContext';
import {
  isPrintMode,
  registerPrintTask,
  completePrintTask,
} from '@/lib/printReadySignal';
import { DemoProvider} from '@/contexts/DemoContext';
import { AppErrorBoundary } from '@/components/layout/AppErrorBoundary';
import { RouteErrorBoundary } from '@/components/layout/RouteErrorBoundary';
import { RequireAuth } from '@/components/layout/RequireAuth';
import { AppLayout } from '@/components/layout/AppLayout';
import { ToastProvider } from '@/components/ui/ToastProvider';
import VersionNudge from '@/components/ui/VersionNudge';
// Eager — small & needed for first paint
import { LoginPage } from '@/pages/auth/LoginPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

// Lazy — QA PERF-3. These pages drag in charts, media-pipe, pdf libs,
// rich-text editors etc. No reason to ship them on first paint.
const PatientListPage = lazyWithReload(() => import('@/pages/patients/PatientListPage').then(m => ({ default: m.PatientListPage })));
const PatientFormPage = lazyWithReload(() => import('@/pages/patients/PatientFormPage').then(m => ({ default: m.PatientFormPage })));
const PatientDetailPage = lazyWithReload(() => import('@/pages/patients/PatientDetailPage').then(m => ({ default: m.PatientDetailPage })));
const AssessmentWizard = lazyWithReload(() => import('@/pages/assessment/AssessmentWizard').then(m => ({ default: m.AssessmentWizard })));
// Clinician PDF SPA-migration Stage B — minimal print scaffold mounted
// under /assessment/:id when ?print=1&type=clinician is on the URL.
// Production clinician PDFs still go through pdfmake; this component is
// reachable only via the debug query `?puppeteer=1&type=clinician` on
// the /api/assessments/:id/report/pdf route.
const ClinicianReportPrint = lazyWithReload(() => import('@/components/clinical/ClinicianReportPrint').then(m => ({ default: m.ClinicianReportPrint })));
const AnalyticsDashboard = lazyWithReload(() => import('@/pages/analytics/AnalyticsDashboard').then(m => ({ default: m.AnalyticsDashboard })));
const FrontDeskPage = lazyWithReload(() => import('@/pages/frontdesk/FrontDeskPage').then(m => ({ default: m.FrontDeskPage })));
// POSTURE-A2c: the front desk posture capture screen, under the assessment (FrontDesk and above).
const PostureCapturePage = lazyWithReload(() => import('@/components/posture/capture/PostureCapturePage').then(m => ({ default: m.PostureCapturePage })));
import { RequireModule } from '@/components/layout/RequireModule';
// Q-5 — AuditLogPage, GdprPage and IntegrationsPage are no longer routes of
// their own: ClinicSettingsPage renders them as sections, so they travel in the
// settings chunk (itself lazy) and these three lazy wrappers are gone.
import { MOVED_ROUTES } from '@/pages/settings/settingsSections';
import { legacyIntegrationTarget } from '@/pages/settings/integrationAnchors';
const ClinicSettingsPage = lazyWithReload(() => import('@/pages/settings/ClinicSettingsPage').then(m => ({ default: m.ClinicSettingsPage })));
// KPI K2 — the owner-only portal the K1-2 sidebar item links to. The server's owner guard is the door.
const KpiPortalPage = lazyWithReload(() => import('@/pages/kpi/KpiPortalPage').then(m => ({ default: m.KpiPortalPage })));
const StaffBoardPage = lazyWithReload(() => import('@/pages/staff/StaffBoardPage').then(m => ({ default: m.StaffBoardPage })));
const ReferralGapsPage = lazyWithReload(() => import('@/pages/referral-gaps/ReferralGapsPage').then(m => ({ default: m.ReferralGapsPage })));
const PortalApp = lazyWithReload(() => import('@/portal/PortalApp').then(m => ({ default: m.PortalApp })));
const PatientIntakePage = lazyWithReload(() => import('@/pages/PatientIntakePage'));
const PatientPortalPage = lazyWithReload(() => import('@/pages/PatientPortalPage').then(m => ({ default: m.PatientPortalPage })));
const PreReexamPage = lazyWithReload(() => import('@/pages/prereexam/PreReexamPage').then(m => ({ default: m.PreReexamPage })));
const ExerciseLibraryPage = lazyWithReload(() => import('@/pages/ExerciseLibraryPage').then(m => ({ default: m.ExerciseLibraryPage })));
const ConditionsPage = lazyWithReload(() => import('@/pages/ConditionsPage').then(m => ({ default: m.ConditionsPage })));
const OnboardingWizard = lazyWithReload(() => import('@/pages/onboarding/OnboardingWizard').then(m => ({ default: m.OnboardingWizard })));
const PricingPage = lazyWithReload(() => import('@/pages/pricing/PricingPage').then(m => ({ default: m.PricingPage })));
const SignupPage = lazyWithReload(() => import('@/pages/auth/SignupPage').then(m => ({ default: m.SignupPage })));
const VerifyEmailPage = lazyWithReload(() => import('@/pages/auth/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));
const SuperAdminDashboard = lazyWithReload(() => import('@/pages/superadmin/SuperAdminDashboard'));

/**
 * Run W / W-3 — /integrations/<id> → the matching anchor in
 * Settings → Integrations. An unknown id lands on the section itself, never on
 * the Settings root.
 */
function LegacyIntegrationRedirect(): JSX.Element {
  const { integrationId } = useParams<{ integrationId: string }>();
  return <Navigate to={legacyIntegrationTarget(integrationId)} replace />;
}

// Tiny loading fallback — matches app shell styling.
function PageLoader(): JSX.Element {
  return (
    <div className="flex items-center justify-center min-h-[40vh]" role="status" aria-label="Loading">
      <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );
}

// Every protected page: auth guard + shared layout + ROUTE-scoped error
// boundary. `RouteErrorBoundary` resets its `hasError` state on every
// pathname change via React's `key` reconciliation. Prior behaviour
// kept `hasError === true` across soft-nav (see Night 8 RCA — the
// boundary instance survived because AppLayout reconciled, so a single
// crash on one route poisoned every subsequent soft-nav).
/**
 * Clinician PDF SPA-migration Stage B — assessment-route dispatcher.
 *
 * When Puppeteer loads `/assessment/:id?print=1&type=clinician`, render
 * the new <ClinicianReportPrint /> component instead of the default
 * AssessmentWizard subtree. This keeps the URL pattern aligned with
 * the patient print URL (`/assessment/:id?print=1&type=patient`),
 * reuses Protected's print-mode bypass + PrintModeBootstrap, and
 * avoids duplicating the wizard's data-loading machinery.
 *
 * Default behaviour (no print param, or `type=patient` / missing
 * type, or any non-print page load) is unchanged: render the
 * AssessmentWizard. The current patient-print path is also unaffected
 * — the `type` query param defaults to 'patient' and ReportGenerator
 * reads it lazily.
 */
function AssessmentRoute(): JSX.Element {
  if (typeof window !== 'undefined' && isPrintMode()) {
    try {
      const type = new URLSearchParams(window.location.search).get('type');
      if (type === 'clinician') {
        return <ClinicianReportPrint />;
      }
    } catch {
      // Fall through to the default wizard render.
    }
  }
  return <AssessmentWizard />;
}

function Protected({ children }: { children: React.ReactNode }): JSX.Element {
  // Bundle 2a.1b — print-mode bypass.
  // When the page loads with `?print=1`, the headless Chromium tab
  // authenticates via the `?token=` URL param (pdf_renderer audience
  // JWT — Bearer-header path falls through to verifyPdfRenderToken in
  // backend middleware). RequireAuth's `/api/auth/me` round-trip
  // would 401 on a pdf_renderer token (the route requires a session
  // shape) and redirect to /login, defeating the render. AppLayout's
  // Sidebar / header also assume a user record. Skipping both is
  // safe — the URL token is the auth boundary; the data-print-ready
  // signal coordinator ensures the page is fully hydrated before
  // Puppeteer captures.
  if (isPrintMode()) {
    return <RouteErrorBoundary>{children}</RouteErrorBoundary>;
  }
  return (
    <RequireAuth>
      <AppLayout>
        <RouteErrorBoundary>{children}</RouteErrorBoundary>
      </AppLayout>
    </RequireAuth>
  );
}

/**
 * Bundle 2a.1b — print-mode body-class toggle + foundational task
 * registrations. Runs once at App mount. When `?print=1` is in the
 * URL: sets `data-print-mode="1"` on <body>, registers the `i18n`
 * and `fonts` print tasks, completes them when their async sources
 * resolve. The signal coordinator (`lib/printReadySignal.ts`)
 * combines these with per-page task registrations (e.g.
 * ReportGenerator's data fetch) and fires
 * `body[data-print-ready="true"]` once all complete.
 *
 * Outside print mode this hook is a no-op — the
 * `printReadySignal.ts` helpers all short-circuit when
 * `isPrintMode()` returns false.
 */
function PrintModeBootstrap(): null {
  useEffect(() => {
    if (!isPrintMode()) return;
    document.body.setAttribute('data-print-mode', '1');

    // Bundle 2a.1b.fix — Referrer-Policy hardening.
    //
    // Print-mode page URL contains the pdf_renderer JWT as
    // `?token=<JWT>`. Without an explicit referrer policy, modern
    // browsers' default ("strict-origin-when-cross-origin") sends the
    // full URL (including query string) in the Referer header on
    // same-origin sub-resource requests. That leaks the token into
    // nginx access logs.
    //
    // Setting `Referrer-Policy: no-referrer` via a meta tag tells the
    // browser to send NO Referer at all from this document. Combined
    // with the parallel nginx `$redacted_referer` map (which scrubs
    // any token that slips through), this is defense-in-depth.
    //
    // The meta tag affects only THIS document. Other tabs / future
    // sessions are unaffected. We restore the tag (or remove it) on
    // unmount for cleanliness — print-mode tabs are ephemeral but the
    // cleanup is cheap.
    let referrerMeta = document.querySelector('meta[name="referrer"]') as HTMLMetaElement | null;
    const hadExistingMeta = referrerMeta !== null;
    const previousContent = referrerMeta?.content ?? '';
    if (!referrerMeta) {
      referrerMeta = document.createElement('meta');
      referrerMeta.name = 'referrer';
      document.head.appendChild(referrerMeta);
    }
    referrerMeta.content = 'no-referrer';

    // Bundle 2a.1c — preload hints for local web fonts.
    //
    // Without preload, the browser only discovers font URLs when it
    // parses the CSS that references them (`@font-face` rules at the
    // top of index.css). The CSS itself loads after HTML parse →
    // fonts load after CSS parse. Adding `<link rel="preload">` in
    // <head> via JS at React mount time starts the font fetch
    // sooner (race the CSS parser).
    //
    // Caveat: useEffect fires AFTER first commit, so first-paint
    // assets already have a head start. This block matters most for
    // post-first-paint renders (HMR re-renders, route changes that
    // re-mount PrintModeBootstrap). For Puppeteer first-render the
    // benefit is small but cheap. The print-ready signal coordinator
    // hard-waits document.fonts.ready, so correctness doesn't depend
    // on this hint — it's purely a latency optimisation.
    const FONT_URLS = [
      '/fonts/Roboto-Regular.woff2',
      '/fonts/Roboto-Bold.woff2',
      '/fonts/Heebo-Regular.woff2',
      '/fonts/Heebo-Bold.woff2',
    ] as const;
    const preloadLinks: HTMLLinkElement[] = [];
    for (const url of FONT_URLS) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'font';
      link.type = 'font/woff2';
      // crossOrigin = 'anonymous' is REQUIRED for font preloads even
      // when same-origin; without it, the browser treats the preload
      // and the @font-face fetch as different requests and re-fetches.
      link.crossOrigin = 'anonymous';
      link.href = url;
      document.head.appendChild(link);
      preloadLinks.push(link);
    }

    // Task: i18n initialised. By the time React mounts, the eager
    // `import './i18n'` at main.tsx:4 has already finished, so this
    // resolves synchronously in 99% of cases. We still register-then-
    // complete to keep the contract robust against any future async-
    // init refactor.
    registerPrintTask('i18n');
    // Dynamic import to avoid coupling App.tsx to i18next directly.
    void import('i18next')
      .then((m) => {
        if (m.default.isInitialized) {
          completePrintTask('i18n');
        } else {
          m.default.on('initialized', () => completePrintTask('i18n'));
        }
      })
      .catch(() => {
        // i18n init failure shouldn't block PDF capture forever —
        // mark the task complete so Puppeteer doesn't time out on
        // an absent fonts/data even when i18n broke.
        completePrintTask('i18n');
      });

    // Task: web fonts loaded.
    //
    // Bundle 2a.1c — per-face readiness check.
    // `document.fonts.ready` resolves when the registry is settled,
    // but a SILENT load failure (404, parse error, CORS) still
    // resolves "ready" — the registry just doesn't contain the face.
    // We explicitly check the 4 expected faces are loadable, and tag
    // the body with a diagnostic attribute if any are missing.
    // Puppeteer reads this attribute post-render to flag the issue
    // (see `_renderUrlInternal` in puppeteerPool.ts).
    //
    // We complete the task EVEN IF some fonts are missing — falling
    // back to system fonts is preferable to hanging the Puppeteer
    // render. The diagnostic is surfaced via the backend logWarn path.
    registerPrintTask('fonts');
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts.ready
        .then(() => {
          const expected = [
            { family: 'Roboto', weight: '400' },
            { family: 'Roboto', weight: '700' },
            { family: 'Heebo', weight: '400' },
            { family: 'Heebo', weight: '700' },
          ] as const;
          const missing = expected.filter(
            (f) => !document.fonts.check(`${f.weight} 1em '${f.family}'`),
          );
          if (missing.length > 0) {
            const tag = missing
              .map((f) => `${f.family} ${f.weight}`)
              .join(', ');
            document.body.setAttribute('data-fonts-missing', tag);
            // eslint-disable-next-line no-console
            console.warn('[printReady] Missing fonts (will fallback):', tag);
          }
          completePrintTask('fonts');
        })
        .catch(() => completePrintTask('fonts'));
    } else {
      // Older browsers without the Font Loading API — treat as
      // complete immediately rather than block.
      completePrintTask('fonts');
    }
    return () => {
      // Cleanup is intentionally minimal — if the page unmounts in
      // print mode, the signal coordinator's state is moot. We do
      // NOT remove the data-print-mode attribute because subsequent
      // re-mounts (HMR in dev) would re-set it anyway.
      //
      // Bundle 2a.1b.fix — restore the referrer meta tag so other
      // application surfaces (HMR navigation in dev, or any future
      // codepath that re-uses this DOM) don't inherit the no-referrer
      // policy. In prod the print-mode tab is ephemeral so this is
      // mostly belt-and-braces.
      if (referrerMeta && referrerMeta.parentNode) {
        if (hadExistingMeta) {
          referrerMeta.content = previousContent;
        } else {
          referrerMeta.parentNode.removeChild(referrerMeta);
        }
      }
      // Bundle 2a.1c — drop the font preload <link> tags so HMR
      // re-mounts in dev don't accumulate duplicates. Prod print-mode
      // tabs are ephemeral; this branch is mostly defensive.
      for (const link of preloadLinks) {
        if (link.parentNode) link.parentNode.removeChild(link);
      }
    };
  }, []);
  return null;
}

function App(): JSX.Element {
  return (
    <AppErrorBoundary>
      <AuthProvider>
        <DemoProvider>
        <ToastProvider />
        <VersionNudge />
        <PrintModeBootstrap />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public */}
            <Route
              path="/login"
              element={
                <AppErrorBoundary>
                  <LoginPage />
                </AppErrorBoundary>
              }
            />

            <Route
              path="/pricing"
              element={
                <AppErrorBoundary>
                  <PricingPage />
                </AppErrorBoundary>
              }
            />
            <Route
              path="/signup"
              element={
                <AppErrorBoundary>
                  <SignupPage />
                </AppErrorBoundary>
              }
            />
            <Route
              path="/verify-email"
              element={
                <AppErrorBoundary>
                  <VerifyEmailPage />
                </AppErrorBoundary>
              }
            />

            {/* Onboarding wizard — auth required but no AppLayout */}
            <Route
              path="/onboarding"
              element={<RequireAuth><OnboardingWizard /></RequireAuth>}
            />

            {/* Protected — redirect to /login if not authenticated */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            <Route
              path="/dashboard"
              element={<Protected><DashboardPage /></Protected>}
            />
            <Route
              path="/patients"
              element={<Protected><PatientListPage /></Protected>}
            />
            <Route
              path="/patients/new"
              element={<Protected><PatientFormPage /></Protected>}
            />
            <Route
              path="/patients/:id"
              element={<Protected><PatientDetailPage /></Protected>}
            />
            <Route
              path="/patients/:id/edit"
              element={<Protected><PatientFormPage /></Protected>}
            />
            <Route
              path="/assessment/new"
              element={<Protected><AssessmentWizard /></Protected>}
            />
            <Route
              path="/assessment/:id"
              element={<Protected><AssessmentRoute /></Protected>}
            />
            <Route
              path="/assessment/:id/posture-capture"
              element={<Protected><PostureCapturePage /></Protected>}
            />
            <Route
              path="/analytics"
              element={<Protected><AnalyticsDashboard /></Protected>}
            />
            {/* Q-4 — the Front Desk module is a clinic setting; a disabled
                clinic redirects to the dashboard instead of rendering it. */}
            <Route
              path="/front-desk"
              element={<Protected><RequireModule module="frontDesk"><FrontDeskPage /></RequireModule></Protected>}
            />
            <Route
              path="/kpi"
              element={<Protected><KpiPortalPage /></Protected>}
            />
            {/* KPI R3 — outside the owner-only portal; the server decides who sees each (404 otherwise). */}
            <Route
              path="/staff-board"
              element={<Protected><StaffBoardPage /></Protected>}
            />
            <Route
              path="/referral-gaps"
              element={<Protected><ReferralGapsPage /></Protected>}
            />
            <Route
              path="/settings"
              element={<Protected><ClinicSettingsPage /></Protected>}
            />
            <Route
              path="/settings/*"
              element={<Protected><ClinicSettingsPage /></Protected>}
            />
            <Route path="/exercises" element={<Protected><ExerciseLibraryPage /></Protected>} />
            <Route path="/conditions" element={<Protected><ConditionsPage /></Protected>} />
            {/* Q-5 — Integrations, the Audit Log and Data & Privacy are Settings
                sections now. The old top-level URLs redirect, so bookmarks,
                old links and anything a clinic pasted into a chat still work.
                The map lives in pages/settings/settingsSections.ts. */}
            {Object.entries(MOVED_ROUTES).map(([from, to]) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}
            {/* Run W / W-3 — the DEEPER legacy links. /integrations redirects
                above, but /integrations/<id> matched NO route at all and fell
                through to the 404 page. Each one now lands on the matching
                block's anchor inside the section. */}
            <Route path="/integrations/:integrationId" element={<LegacyIntegrationRedirect />} />
            <Route
              path="/superadmin"
              element={<Protected><SuperAdminDashboard /></Protected>}
            />
            {/* Patient Intake — public, no auth */}
            <Route path="/intake/:token" element={<PatientIntakePage />} />
            {/* Pre-re-exam form — public, no auth */}
            <Route path="/prereexam/:token" element={<PreReexamPage />} />

            {/* Patient BPS Portal — public, no login, standalone */}
            <Route path="/bps-portal/:token" element={<PatientPortalPage />} />
            {/* Patient Portal — public, no clinician auth */}
            <Route path="/portal/*" element={<PortalApp />} />

            <Route
              path="*"
              element={
                <AppErrorBoundary>
                  <NotFoundPage />
                </AppErrorBoundary>
              }
            />
          </Routes>
          </Suspense>
        </BrowserRouter>
        </DemoProvider>
      </AuthProvider>
    </AppErrorBoundary>
  );
}

export default App;
